import { FetchWithAuth } from '../auth';
import env from '../configs/env';
import { FileUploadParams } from './file-upload.service';

export const CHUNK_SIZE_THRESHOLD = 5 * 1024 * 1024; // 5 MB
export const CHUNK_SIZE = 5 * 1024 * 1024;           // 5 MB
export const MAX_CHUNK_RETRIES = 3;

const CHUNKED_BASE = '/file/chunked';

interface InitSessionResponse {
  sessionId: string;
}

/**
 * Delays execution for the specified number of milliseconds.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Determines whether a failed chunk upload should be retried.
 * Retries on 500+ server errors and network errors.
 * Fails fast on 404 (expired session) and 409 (aborted session).
 */
function isRetryableError(status: number | null): boolean {
  if (status === 404 || status === 409) {
    return false;
  }
  // Network errors (status is null) or server errors (500+) are retryable
  return status === null || status >= 500;
}


/**
 * Uploads a single chunk with retry logic and exponential backoff.
 * Retries on 500/network errors up to MAX_CHUNK_RETRIES times.
 * Fails fast on 404 (expired session) and 409 (aborted session).
 */
async function uploadChunkWithRetry(
  url: string,
  formData: FormData,
  fetchWithAuth: FetchWithAuth,
): Promise<void> {
  for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
    let status: number | null = null;
    try {
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        return;
      }
      status = response.status;
    } catch (error: unknown) {
      // Network error — status stays null (retryable)
      console.error('Chunk upload network error', error);
    }

    if (!isRetryableError(status)) {
      throw new Error('Upload failed');
    }

    if (attempt < MAX_CHUNK_RETRIES) {
      await delay(attempt * 1000); // Exponential backoff: 1s, 2s, 3s
    }
  }

  throw new Error('Upload failed');
}

/**
 * Attempts to abort a chunked upload session.
 * Errors during abort are logged but not re-thrown.
 */
async function abortSession(
  sessionId: string,
  fetchWithAuth: FetchWithAuth,
): Promise<void> {
  try {
    await fetchWithAuth(
      `${env.api.host}${CHUNKED_BASE}/abort?sessionId=${encodeURIComponent(sessionId)}`,
      { method: 'POST' },
    );
  } catch (error: unknown) {
    console.error('Failed to abort chunked upload session', error);
  }
}

export class ChunkedUploadService {
  /**
   * Uploads a file using the chunked upload protocol.
   *
   * 1. Initializes a session via POST /chunked/init
   * 2. Slices the file into CHUNK_SIZE chunks
   * 3. Uploads each chunk sequentially (fresh DPoP per chunk via fetchWithAuth)
   * 4. Completes the session via POST /chunked/complete
   * 5. On failure after retries, aborts the session via POST /chunked/abort
   */
  static async uploadFileChunked(
    params: FileUploadParams,
    fetchWithAuth: FetchWithAuth,
    onProgress?: (percent: number) => void,
  ): Promise<void> {
    const { cedant, fileName, contentType, file } = params;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    // Step 1: Initialize upload session
    const initParams = new URLSearchParams({
      cedant,
      fileName,
      contentType,
      totalChunks: String(totalChunks),
    });

    const initResponse = await fetchWithAuth(
      `${env.api.host}${CHUNKED_BASE}/init?${initParams.toString()}`,
      { method: 'POST' },
    );

    if (!initResponse.ok) {
      throw new Error('Upload failed');
    }

    const { sessionId } = (await initResponse.json()) as InitSessionResponse;

    // Step 2 & 3: Slice file and upload chunks sequentially
    let completedChunks = 0;
    if (onProgress) {
      onProgress(0);
    }

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkBlob = file.slice(start, end);

        const formData = new FormData();
        formData.append('sessionId', sessionId);
        formData.append('chunkIndex', String(chunkIndex));
        formData.append('chunk', chunkBlob);

        await uploadChunkWithRetry(
          `${env.api.host}${CHUNKED_BASE}/upload`,
          formData,
          fetchWithAuth,
        );

        completedChunks++;
        if (onProgress) {
          onProgress(Math.floor((completedChunks / totalChunks) * 100));
        }
      }
    } catch (error: unknown) {
      // All retries exhausted or non-retryable error — abort the session
      console.error('Chunked upload failed, aborting session', error);
      await abortSession(sessionId, fetchWithAuth);
      throw new Error('Upload failed');
    }

    // Step 4: Complete the upload
    const completeResponse = await fetchWithAuth(
      `${env.api.host}${CHUNKED_BASE}/complete?sessionId=${encodeURIComponent(sessionId)}`,
      { method: 'POST' },
    );

    if (!completeResponse.ok) {
      throw new Error('Upload failed');
    }
  }
}
